import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNextQuizRecommendation } from '../../services/authService';
import { buildVerifyPath } from '../../quiz/pendingQuiz';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import styles from './NextUpCard.module.css';

/**
 * Zero-coin activation card for Home. Shows ONE specific, quiz-ready title
 * (onboarding pick → popular → curated) with a direct "Start Quiz" that goes
 * through the canonical /verify route. Falls back to "Choose a Movie" when the
 * server has no safe recommendation. Never recommends unavailable titles.
 */
export default function NextUpCard() {
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, recommendation: null });
    const shownRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        getNextQuizRecommendation()
            .then((data) => { if (!cancelled) setState({ loading: false, recommendation: data?.recommendation ?? null }); })
            .catch(() => { if (!cancelled) setState({ loading: false, recommendation: null }); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (state.loading || shownRef.current) return;
        shownRef.current = true;
        trackEvent(ANALYTICS_EVENTS.HOME_NEXT_UP_SHOWN, { platform: 'web', has_recommendation: !!state.recommendation, source: state.recommendation?.source ?? 'none' });
    }, [state]);

    if (state.loading) return <section className={`${styles.card} ${styles.skeleton}`} aria-busy="true" />;

    const rec = state.recommendation;
    const handleClick = () => {
        trackEvent(ANALYTICS_EVENTS.HOME_NEXT_UP_CLICKED, { platform: 'web', has_recommendation: !!rec, source: rec?.source ?? 'none' });
        if (rec) {
            navigate(buildVerifyPath(rec.kind, rec.tmdbId, rec.seasonNumber), { state: { sourceScreen: 'home_next_up', mediaTitle: rec.title } });
        } else {
            navigate('/discover');
        }
    };

    const title = rec
        ? `You've seen ${rec.title}${rec.kind === 'tv' && rec.seasonNumber != null ? ` · Season ${rec.seasonNumber}` : ''}?`
        : 'Earn your first coin';

    return (
        <section className={styles.card}>
            {rec?.posterUrl ? <img src={rec.posterUrl} alt="" className={styles.poster} /> : <div className={styles.posterFallback}>🪙</div>}
            <div className={styles.body}>
                <p className={styles.eyebrow}>⚡ Earn your first coin</p>
                <h2 className={styles.title}>{title}</h2>
                <p className={styles.sub}>
                    {rec ? <>8 questions · pass with 5<br />Prove it and earn your first Bronze coin.</> : 'Pick something you know well.'}
                </p>
                <button className={styles.cta} onClick={handleClick}>{rec ? 'Start Challenge' : 'Choose a Movie'} →</button>
            </div>
        </section>
    );
}
